import { Icon, Spinner, SpinnerSize, IconButton } from '@fluentui/react';
import { Props } from "react-select";
import styled from "@emotion/styled";
import React from "react";
import { Theme } from "@emotion/react";

const ChevronIcon = styled(Icon)`
  display: inline-block;
  -webkit-font-smoothing: antialiased;
  font-style: normal;
  font-weight: normal;
  font-family: FabricMDL2Icons;
  font-size: 12px;
  pointer-events: none;
  margin: 0px 10px;
  color: ${p => p.theme.palette.neutralSecondary};
`;

const ClearIcon = styled.div`
  height: 100%;
  display: flex;
  align-items: center;
  padding: 0 10px;
  i {
    font-size: 11px;
    color: ${p => p.theme.palette.neutralSecondary};
  }
`;

const MultiValueRemoveIconButton = styled(IconButton)`
  height: 100%;
  border-radius: 0px 2px 2px 0px;
  i {
    font-size: 12px;
  }
`;

const theme = (theme: any, myTheme: Theme) => {
  return {
    ...theme,
    colors: {
      ...theme.colors,
      neutral0: myTheme.palette.white,
      neutral5: myTheme.palette.neutralLighter,
      neutral10: myTheme.palette.neutralLight,
      neutral30: myTheme.palette.neutralTertiary,
      neutral40: myTheme.palette.neutralSecondaryAlt,
      neutral50: myTheme.palette.neutralSecondaryAlt,
      neutral60: myTheme.palette.neutralSecondary,
      neutral70: myTheme.palette.neutralPrimaryAlt,
      neutral80: myTheme.palette.neutralPrimary,
      neutral90: myTheme.palette.neutralDark,
      primary: myTheme.palette.themePrimary,
      primary75: myTheme.palette.themeSecondary,
      primary50: myTheme.palette.themeTertiary,
      primary25: myTheme.palette.themeLight
    },
    borderRadius: 2
  };
};

const components: any = {
  DropdownIndicator: () => <ChevronIcon iconName="ChevronDown" />,
  ClearIndicator: ({ innerProps }: { innerProps: any }) => {
    return (
      <ClearIcon>
        <Icon {...innerProps} iconName="ChromeClose" />
      </ClearIcon>
    );
  },
  LoadingIndicator: () => {
    return (
      <Spinner
        size={SpinnerSize.medium}
        styles={{ root: { marginRight: 9 } }}
      />
    );
  },
  MultiValueRemove: (props: any) => {
    return (
      <MultiValueRemoveIconButton
        iconProps={{ iconName: "Cancel" }}
        onClick={props.innerProps.onClick}
      />
    );
  }
};
const styles = (myTheme: Theme) =>
  ({
    control: (p, s) => {
      return {
        ...p,
        minHeight: 32,
        boxShadow: "none",
        outline:
          s.isFocused && !s.menuIsOpen
            ? `2px solid ${myTheme.palette.themePrimary}`
            : "none",
        outlineOffset: -1,
        "&:hover": {
          borderColor: myTheme.palette.neutralPrimary
        }
      };
    },
    placeholder: p => ({
      ...p,
      fontSize: 14
    }),
    valueContainer: (p, s) => {
      return {
        ...p,
        padding: s.isMulti ? (s.hasValue ? 0 : "0 8px") : "0 8px"
      };
    },
    menu: p => {
      return {
        ...p,
        marginTop: 0,
        border: 0,
        boxShadow:
          "rgba(0, 0, 0, 0.133) 0px 3.2px 7.2px 0px, rgba(0, 0, 0, 0.11) 0px 0.6px 1.8px 0px"
      };
    },
    menuList: p => {
      return {
        ...p,
        padding: 0,
        border: 0
      };
    },
    indicatorSeparator: p => {
      return {
        ...p,
        width: 0.8
      };
    },
    singleValue: p => {
      return {
        ...p,
        fontSize: 14
      };
    },
    multiValue: p => {
      return {
        ...p,
        height: 26,
        backgroundColor: myTheme.palette.neutralLighter,
        "&:hover": {
          backgroundColor: myTheme.palette.neutralLight
        }
      };
    },
    option: (p, s) => {
      return {
        ...p,
        fontSize: 14,
        color: s.isDisabled
          ? myTheme.palette.neutralTertiary
          : myTheme.palette.neutralPrimary,
        background: getBackground(s, myTheme),
        "&:hover": {
          outline: "none",
          background: getHoverBackground(s, myTheme)
        }
      };
    },
    input: p => {
      return {
        ...p,
        fontSize: 14
      };
    },
    noOptionsMessage: p => {
      return {
        ...p,
        fontSize: 14
      };
    },
    loadingMessage: p => {
      return {
        ...p,
        fontSize: 14
      };
    },
    multiValueLabel: p => {
      return {
        ...p,
        lineHeight: "26px",
        padding: 0,
        paddingLeft: 6,
        paddingRight: 3
      };
    }
  } as Props["styles"]);

export default { styles, theme, components };

const getBackground = (s: any, myTheme: Theme) => {
  const { isDisabled, isSelected, isFocused } = s;

  let backgroundColor = "transparent";

  if (isSelected) {
    backgroundColor = myTheme.isDark ? "#3b3a39" : myTheme.palette.neutralLight;
    return backgroundColor;
  }

  if (isDisabled) {
    backgroundColor = "transparent";
    return backgroundColor;
  }

  if (isFocused) {
    backgroundColor = myTheme.isDark
      ? "#201f1e"
      : myTheme.palette.neutralLighter;
    return backgroundColor;
  }

  return backgroundColor;
};

const getHoverBackground = (s: any, myTheme: Theme) => {
  const { isDisabled, isSelected } = s;

  let backgroundColor = myTheme.isDark
    ? "#201f1e"
    : myTheme.palette.neutralLighter;

  if (isSelected) {
    backgroundColor = myTheme.isDark ? "#3b3a39" : myTheme.palette.neutralLight;
    return backgroundColor;
  }

  if (isDisabled) {
    backgroundColor = "transparent";
    return backgroundColor;
  }

  return backgroundColor;
};
